import { Button, Switch } from '@iwoca/orion';
import { Trans } from '@lingui/macro';
import { Column } from '@tanstack/react-table';
import { useApplicationsTableState } from 'components/Dashboard/ApplicationsTable/ApplicationsTableState.context';
import { useAmplitude } from 'hooks/useAmplitude';
import { TApplication } from 'lendingApi/applications.types';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import styles from './ColumnToggles.module.css';

export const ColumnToggles = () => {
  const { trackEvent } = useAmplitude();
  const { table } = useApplicationsTableState();

  const resetColumnVisibility = () => {
    table.getAllColumns().forEach((column) => column.toggleVisibility(true));
    trackEvent(AMPLITUDE_EVENTS.showAllColumns);
  };

  const handleClick = (
    label: string | undefined,
    column: Column<TApplication>,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    const toggle = column.getToggleVisibilityHandler();
    const currentlyVisible = column.getIsVisible();
    toggle(event);
    trackEvent(AMPLITUDE_EVENTS.columnToggle, {
      Column: label,
      Display: currentlyVisible ? 'off' : 'on',
    });
  };

  return (
    <div className={styles.columnSettings}>
      <h3 className={styles.heading}>
        <Trans>Customise columns</Trans>
      </h3>
      <p className={styles.subHeading}>
        <Trans>
          Change what columns you want to display or drag to reorder the column.
        </Trans>
      </p>
      <div className={styles.columnList}>
        {table
          .getAllLeafColumns()
          .filter((column) => column.columnDef.enableHiding !== false)
          .map((column) => {
            const renderLabel = () =>
              typeof column.columnDef.header === 'function'
                ? column.id
                : column.columnDef.header;

            const label = renderLabel();

            return (
              <div className={styles.item} key={column.id}>
                <span>{label}</span>
                <Switch
                  data-testid={`display-toggle-${column.id}`}
                  checked={column.getIsVisible()}
                  onClick={(event) => handleClick(label, column, event)}
                />
              </div>
            );
          })}
      </div>
      <Button
        onClick={resetColumnVisibility}
        className={styles.displayAllButton}
        variant="secondary-alt"
      >
        <Trans>Reset columns to default</Trans>
      </Button>
    </div>
  );
};
